import { createContext, useContext, type ReactNode } from "react";
import type { AuthUseCases } from "@gui/infrastructure/composition/auth";
import type { CompaniesUseCases } from "@app/company";
import type { ImportSettingsUseCases } from "@app/imports";
import type { DuplicateDealsUseCases } from "@app/deals";
import type { IntegrationsUseCases } from "@gui/infrastructure/composition/integrations";
import type { MissionControlUseCases } from "@gui/infrastructure/composition/missionControl";
import type { PasswordGeneratorUseCases } from "@app/teamMembers";
import type { LabelsUseCases } from "@app/labels";
import type { MissionsUseCases } from "@app/missions";
import type { JobUseCases } from "@gui/infrastructure/composition/jobs";
import type { ShiftsUseCases } from "@app/shifts";
import type { BranchMatcherUseCases } from "@app/branchMatcher";
import type { DealMatcherUseCases } from "@app/dealMatcher";

/**
 * Aggregate handle for every wired-up use case in the running app. GUI's
 * composition root (`5-UI/GUI/infrastructure/composition/*`) builds these
 * and provides them via `UseCasesProvider`; components consume individual
 * use cases through the per-use-case hooks below.
 *
 * Tests wrap their render in a `<UseCasesProvider value={...}>` (or a test
 * helper) with port-mocked instances.
 */
export interface UseCases {
	auth: AuthUseCases;
	companies: CompaniesUseCases;
	jobs: JobUseCases;
	shifts: ShiftsUseCases;
	missions: MissionsUseCases;
	branchMatcher: BranchMatcherUseCases;
	dealMatcher: DealMatcherUseCases;
	importSettings: ImportSettingsUseCases;
	duplicateDeals: DuplicateDealsUseCases;
	integrations: IntegrationsUseCases;
	missionControl: MissionControlUseCases;
	passwordGenerator: PasswordGeneratorUseCases;
	labels: LabelsUseCases;
}

const UseCasesContext = createContext<UseCases | null>(null);

export function UseCasesProvider({ value, children }: { value: UseCases; children: ReactNode }) {
	return <UseCasesContext.Provider value={value}>{children}</UseCasesContext.Provider>;
}

function useUseCases(): UseCases {
	const ctx = useContext(UseCasesContext);
	if (!ctx) {
		throw new Error("useUseCases must be called inside <UseCasesProvider>");
	}
	return ctx;
}

export function useAuthUseCases(): AuthUseCases {
	return useUseCases().auth;
}

export function useCompaniesUseCases(): CompaniesUseCases {
	return useUseCases().companies;
}

export function useJobUseCases(): JobUseCases {
	return useUseCases().jobs;
}

export function useShiftsUseCases(): ShiftsUseCases {
	return useUseCases().shifts;
}

export function useMissionsUseCase(): MissionsUseCases {
	return useUseCases().missions;
}

export function useBranchMatcherUseCases(): BranchMatcherUseCases {
	return useUseCases().branchMatcher;
}

export function useDealMatcherUseCases(): DealMatcherUseCases {
	return useUseCases().dealMatcher;
}

export function useImportSettingsUseCases(): ImportSettingsUseCases {
	return useUseCases().importSettings;
}

export function useDuplicateDealsUseCases(): DuplicateDealsUseCases {
	return useUseCases().duplicateDeals;
}

export function useIntegrationsUseCases(): IntegrationsUseCases {
	return useUseCases().integrations;
}

export function useMissionControlUseCases(): MissionControlUseCases {
	return useUseCases().missionControl;
}

export function usePasswordGeneratorUseCases(): PasswordGeneratorUseCases {
	return useUseCases().passwordGenerator;
}

export function useLabelsUseCases(): LabelsUseCases {
	return useUseCases().labels;
}
